/**
 * Search-index sidecars for workshop captures / uploads.
 *
 * Binary blobs (PNG captures, ZIP/audio uploads) are skipped by the
 * ingestion extractor. Seed deliverables already use
 * `workshops/<wsId>/_index/<id>.md` (see
 * `infra/backfill-deliverable-search-index.mjs`); this module is the
 * runtime equivalent so facilitator-added content becomes searchable
 * without waiting for a backfill script.
 *
 * AssetLocker / Captures `sync()` only list `uploads/` / `captures/`,
 * so `_index/` sidecars never appear as phantom chips.
 */

import { isWorkshopBffEnabled } from "@/env";
import { deleteDoc, uploadDoc } from "@/lib/docs-api";
import type { Workshop } from "@/content/workshops";

const TIER = "workshops";

/** Extensions the ingestion worker can extract text from (mirrors
 *  `services/ingestion/app/extract.py`). */
export const EXTRACTABLE_EXTS = new Set([
  "md",
  "markdown",
  "txt",
  "csv",
  "log",
  "json",
  "pdf",
  "docx",
  "xlsx",
  "pptx",
]);

export function searchIndexPath(
  workshopId: Workshop["id"] | string,
  id: string,
): string {
  return `${workshopId}/_index/${id}.md`;
}

export function isExtractableExt(extOrFilename: string): boolean {
  const raw = extOrFilename.includes(".")
    ? extOrFilename.split(".").pop() ?? ""
    : extOrFilename;
  return EXTRACTABLE_EXTS.has(raw.toLowerCase().replace(/^\./, ""));
}

export function isExtractableMime(mime: string | undefined): boolean {
  if (!mime) return false;
  const lower = mime.toLowerCase();
  if (lower.startsWith("text/")) return true;
  if (lower === "application/json") return true;
  if (lower === "application/pdf") return true;
  if (lower.includes("wordprocessingml")) return true;
  if (lower.includes("spreadsheetml")) return true;
  if (lower.includes("presentationml")) return true;
  return false;
}

function tagsLine(tags: string[] | undefined): string {
  const cleaned = (tags ?? []).map((t) => t.trim()).filter(Boolean);
  return cleaned.length ? cleaned.join(", ") : "";
}

/** Markdown body for a capture sidecar (title/caption/tags are the
 *  only searchable signal for PNG/screenshot blobs). */
export function captureIndexMarkdown(input: {
  workshopId: string;
  id: string;
  title: string;
  caption?: string;
  kind?: string;
  activity?: string;
  tags?: string[];
}): string {
  const lines = [
    `# Capture: ${input.title.trim() || input.id}`,
    "",
    `Workshop: ${input.workshopId}`,
    `Capture id: ${input.id}`,
  ];
  if (input.kind) lines.push(`Kind: ${input.kind}`);
  if (input.activity) lines.push(`Activity: ${input.activity}`);
  const tags = tagsLine(input.tags);
  if (tags) lines.push(`Tags: ${tags}`);
  const caption = (input.caption ?? "").trim();
  if (caption) {
    lines.push("", "## Caption", "", caption);
  }
  lines.push("");
  return lines.join("\n");
}

/** Markdown body for an upload sidecar.
 *
 *  Extractable uploads get a short metadata-only sidecar (label + tags)
 *  so tag edits are searchable without duplicating PDF/DOCX body text.
 *  Non-extractable uploads get the same metadata plus an explicit note
 *  that the binary itself is not indexed.
 */
export function uploadIndexMarkdown(input: {
  workshopId: string;
  id: string;
  label: string;
  mimeType?: string;
  format?: string;
  tags?: string[];
  extractable: boolean;
}): string {
  const lines = [
    `# Upload: ${input.label.trim() || input.id}`,
    "",
    `Workshop: ${input.workshopId}`,
    `Upload id: ${input.id}`,
  ];
  if (input.format) lines.push(`Format: ${input.format}`);
  if (input.mimeType) lines.push(`MIME: ${input.mimeType}`);
  const tags = tagsLine(input.tags);
  if (tags) lines.push(`Tags: ${tags}`);
  if (!input.extractable) {
    lines.push(
      "",
      "Binary asset — file contents are not text-extractable; this sidecar is the searchable index entry.",
    );
  }
  lines.push("");
  return lines.join("\n");
}

/** Best-effort upsert. No-op when BFF workshop backend is off. Never throws. */
export async function upsertSearchIndex(
  workshopId: Workshop["id"] | string,
  id: string,
  markdown: string,
): Promise<void> {
  if (!isWorkshopBffEnabled()) return;
  const path = searchIndexPath(workshopId, id);
  const blob = new Blob([markdown], { type: "text/markdown" });
  try {
    await uploadDoc(TIER, path, blob);
  } catch {
    /* fire-and-forget — primary blob sync already owns the user-visible error */
  }
}

/** Best-effort delete. No-op when BFF workshop backend is off. Never throws. */
export async function deleteSearchIndex(
  workshopId: Workshop["id"] | string,
  id: string,
): Promise<void> {
  if (!isWorkshopBffEnabled()) return;
  const path = searchIndexPath(workshopId, id);
  try {
    await deleteDoc(TIER, path);
  } catch {
    /* best-effort — orphan sidecar is eventually consistent noise only */
  }
}
